import { productSearch } from "@bloomreach/discovery-web-sdk";
import { createInfiniteQuery, keepPreviousData } from "@tanstack/svelte-query";
import { debounce } from "es-toolkit";
import { emit } from "~/lib/events";
import {
  resolveBloomreachCredentials,
  toSdkConfiguration,
  BLOOMREACH_STALE_TIME_MS,
} from "~/lib/bloomreach/config";
import { buildProductSearchOptions } from "~/lib/bloomreach/query-builders";
import { nextPageStart, parseBloomreachResponse } from "~/lib/bloomreach/parse-response";
import { toSafeRedirectUrl } from "~/lib/safe-url";
import { getSearchQuery, pushSearchState, replaceSearchState } from "~/lib/url-state";
import { createListingState } from "./create-listing-state.svelte";
import { syncListingUrl } from "./sync-listing-url.svelte";
import { trackListingEvents } from "./track-listing-events.svelte";

export interface SearchListingConfig {
  cardSelector: string;
  accountId?: string;
  domainKey?: string;
  rows?: number;
}

export function createSearchListing(config: SearchListingConfig) {
  const { accountId, domainKey } = resolveBloomreachCredentials(config);
  const rows = config.rows ?? 48;
  const credentialsPresent = Boolean(accountId && domainKey);

  const initialSearchQuery = getSearchQuery();
  let _pendingSearchQuery: string = $state(initialSearchQuery);
  let _debouncedSearchQuery: string = $state(initialSearchQuery);
  const _debouncedSearchUpdate = debounce((v: string) => {
    _debouncedSearchQuery = v;
  }, 300);
  $effect(() => {
    _debouncedSearchUpdate(_pendingSearchQuery);
    return () => _debouncedSearchUpdate.cancel();
  });
  const activeSearchQuery = {
    get current() {
      return _debouncedSearchQuery;
    },
    setImmediately(v: string) {
      _debouncedSearchUpdate.cancel();
      _debouncedSearchQuery = v;
    },
  };

  // _adoptedSearchQuery tracks the query that should appear in the URL. It equals
  // activeSearchQuery.current normally, but is overridden to the corrected term
  // when Bloomreach autocorrects (so filter/sort URL pushes use the corrected term,
  // not the user's typo) and is restored on revert.
  let _adoptedSearchQuery: string = $state(initialSearchQuery);

  let _rawAutocorrectQuery: string | undefined = $state(undefined);
  let _rawDidYouMean: string[] | undefined = $state(undefined);
  let _autocorrectSuppressed: boolean = $state(false);
  let _spellcorrectOff: boolean = $state(false);

  const listing = createListingState({
    cardSelector: config.cardSelector,
    sessionKey: `search-card-index:${window.location.pathname}`,
    resolvedRows: () => rows,
    credentialsPresent,
    onScrollStop: (f, s, start) => replaceSearchState(f, s, start, _adoptedSearchQuery),
    onError: (reason) => emit("search:query_error", { reason }),
    makeQuery: ({ filters, sort, initialRows }) =>
      createInfiniteQuery(() => ({
        queryKey: [
          "search-products",
          {
            accountId,
            domainKey,
            searchQuery: activeSearchQuery.current,
            activeFilters: filters(),
            activeSort: sort(),
            initialRows,
            rows,
            spellcorrectOff: _spellcorrectOff,
          },
        ],
        enabled: credentialsPresent && activeSearchQuery.current !== "",
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
          const sdkConfig = toSdkConfiguration({ accountId, domainKey });
          const options = {
            ...buildProductSearchOptions({
              searchQuery: activeSearchQuery.current,
              currentUrl: window.location.href,
              rows: pageParam === 0 ? initialRows : rows,
              start: pageParam,
              activeFilters: filters(),
              sort: sort(),
            }),
            ...(_spellcorrectOff ? { "query.spellcorrect": "off" as const } : {}),
          };
          const response = await productSearch(activeSearchQuery.current, sdkConfig, options);
          return parseBloomreachResponse(response);
        },
        getNextPageParam: nextPageStart,
        placeholderData: keepPreviousData,
        staleTime: BLOOMREACH_STALE_TIME_MS,
      })),
  });

  syncListingUrl(listing, {
    push: (f, s) => pushSearchState(f, s, _adoptedSearchQuery),
    onPopstate: () => {
      const restoredQuery = getSearchQuery();
      _adoptedSearchQuery = restoredQuery;
      _pendingSearchQuery = restoredQuery;
      activeSearchQuery.setImmediately(restoredQuery);
    },
  });

  trackListingEvents(listing, {
    onPageViewed: (resultCount, filters, sort, firstPage) => {
      emit("search:page_viewed", {
        resultCount,
        searchQuery: activeSearchQuery.current,
        initialFilters: filters,
        initialSort: sort,
      });
      if (firstPage?.autoCorrectQuery) {
        emit("search:autocorrected", {
          original: activeSearchQuery.current,
          corrected: firstPage.autoCorrectQuery,
        });
      }
    },
    onFiltersChanged: (f) => emit("search:filters_changed", { filters: f }),
    // The query is disabled while there's no search term (see `enabled` above) and will
    // never resolve on its own — fire page_viewed immediately for that landing state.
    viewedImmediately: () => activeSearchQuery.current === "",
  });

  // Keep _adoptedSearchQuery in sync with the typed query and reset autocorrect
  // suppression whenever the user submits a new search.
  $effect(() => {
    const q = activeSearchQuery.current;
    _adoptedSearchQuery = q;
    _autocorrectSuppressed = false;
    _spellcorrectOff = false;
  });

  // Track autocorrect and DYM state from the response, and update the URL to
  // the corrected term when Bloomreach autocorrects the query.
  // Guards:
  //   isPlaceholderData — skip stale data while a new fetch is in-flight
  //   getSearchQuery()  — skip if URL already carries the corrected term (idempotent)
  $effect(() => {
    if (listing.query.isPlaceholderData) return;
    const page0 = listing.query.data?.pages[0];
    const corrected = page0?.autoCorrectQuery;
    _rawAutocorrectQuery = corrected;
    _rawDidYouMean = page0?.did_you_mean;
    if (_autocorrectSuppressed || !corrected || corrected === getSearchQuery()) return;
    _adoptedSearchQuery = corrected;
    replaceSearchState(listing.activeFilters.current, listing.activeSort, 0, corrected);
  });

  // Navigate immediately when Bloomreach returns a merchandising redirect.
  // Must be $effect.pre, not $effect: pre-effects run before trackListingEvents's
  // regular $effect, so search:page_viewed is never dispatched on a redirect response.
  // In production window.location.href tears the page down immediately; in tests
  // (hash-URL navigation keeps the page alive) the pre-ordering is what prevents
  // the analytics effect from also firing.
  $effect.pre(() => {
    const redirect = listing.query.data?.pages[0]?.keywordRedirect;
    if (!redirect) return;
    const safeUrl = toSafeRedirectUrl(redirect["redirected url"]);
    if (!safeUrl) {
      // Do not emit the rejected raw URL to analytics — just the reason.
      emit("search:redirected", { reason: "unsafe_url" });
      return;
    }
    emit("search:redirected", { url: safeUrl });
    window.location.href = safeUrl;
  });

  // Replace history entry when the adopted query or filter/sort changes. Using
  // _adoptedSearchQuery (not activeSearchQuery.current) ensures autocorrected
  // searches don't get silently reverted by a subsequent filter/sort write.
  $effect(() => {
    const adopted = _adoptedSearchQuery;
    if (adopted === getSearchQuery()) return;
    replaceSearchState(listing.activeFilters.current, listing.activeSort, 0, adopted);
  });

  return {
    listing,
    get pendingSearchQuery(): string {
      return _pendingSearchQuery;
    },
    set pendingSearchQuery(v: string) {
      _pendingSearchQuery = v;
    },
    get searchQuery(): string {
      return activeSearchQuery.current;
    },
    get autocorrectQuery(): string | undefined {
      return _autocorrectSuppressed ? undefined : _rawAutocorrectQuery;
    },
    get didYouMean(): string[] | undefined {
      return _rawDidYouMean;
    },
    setQueryImmediately(q: string) {
      _pendingSearchQuery = q;
      activeSearchQuery.setImmediately(q);
    },
    async handleLoadMore() {
      const result = await listing.handleLoadMore();
      if (result) emit("search:load_more", result);
    },
    handleSortChange(v: string) {
      if (listing.handleSortChange(v)) emit("search:sort_changed", { sort: v });
    },
    handleReset() {
      listing.resetAll();
      emit("search:all_cleared");
    },
    handleSuggestionClick(suggestion: string) {
      _pendingSearchQuery = suggestion;
      activeSearchQuery.setImmediately(suggestion);
      listing.pendingFilters = {};
      listing.setFiltersImmediately({});
      listing.activeSort = "";
      listing.clearScrollSession();
      emit("search:did_you_mean_clicked", { suggestion });
    },
    revertAutocorrect() {
      if (!_rawAutocorrectQuery) return;
      const corrected = _rawAutocorrectQuery;
      _autocorrectSuppressed = true;
      _spellcorrectOff = true;
      _adoptedSearchQuery = activeSearchQuery.current;
      // Filters and sort are intentionally preserved — this is a search-parameter
      // change (disable spellcorrect), not a new search like a DYM suggestion click.
      replaceSearchState(
        listing.activeFilters.current,
        listing.activeSort,
        0,
        activeSearchQuery.current,
      );
      emit("search:autocorrect_reverted", {
        original: activeSearchQuery.current,
        corrected,
      });
    },
  };
}

export type SearchListingState = ReturnType<typeof createSearchListing>;
